import React, { useState, useEffect } from 'react';
import { RxCross2 } from 'react-icons/rx';
import { FiTrash2, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { updateMessage, deleteMessage, fetchNoteEditHistory } from '~/services/campaign';
import { toast } from 'react-hot-toast';
import Toggle from '~/components/Toggle';
import { formatDateIfValid } from '~/utils';
import { linkifyUrls } from '~/utils/linkifyUrls';

interface Note {
    id: number;
    created_date: string;
    created_by_name: string;
    note: string;
    tags?: string;
    pinned_flag?: boolean;
    assignee?: string;
}

interface EditHistory {
    id: number;
    note_content: string;
    edited_by_name: string;
    edited_date: string;
}

interface NotesModalProps {
    notes?: string;
    note?: Note | null;
    created_by_name?: string;
    created_date?: string;
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    campaignId?: number;
    onPinToggle?: (updatedNote: Note) => void;
    onDelete?: (noteId: number) => void;
    customDeleteFn?: (noteId: number) => Promise<Response>;
    customFetchHistoryFn?: (noteId: number) => Promise<any[]>;
}

const NotesModal: React.FC<NotesModalProps> = ({
    notes: legacyNotes,
    note,
    created_by_name,
    created_date,
    isOpen,
    onClose,
    title,
    campaignId,
    onPinToggle,
    onDelete,
    customDeleteFn,
    customFetchHistoryFn,
}) => {
    const [isPinning, setIsPinning] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [editHistory, setEditHistory] = useState<EditHistory[]>([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [expandedHistoryId, setExpandedHistoryId] = useState<number | null>(null);
    // Optimistic UI state for the pin toggle
    const [optimisticPinnedFlag, setOptimisticPinnedFlag] = useState(
        note?.pinned_flag || false
    );

    // Support both old and new API
    const displayNotes = note?.note || legacyNotes || '';
    const displayTitle =
        title || (note ? `Note - ${note.created_by_name}` : 'Note');

    // Sync optimistic state with prop changes
    useEffect(() => {
        setOptimisticPinnedFlag(note?.pinned_flag || false);
    }, [note?.pinned_flag]);

    // Load edit history when modal opens
    useEffect(() => {
        if (isOpen && note && (campaignId || customFetchHistoryFn)) {
            setExpandedHistoryId(null);
            setLoadingHistory(true);
            const fetchHistory = customFetchHistoryFn
                ? customFetchHistoryFn(note.id)
                : fetchNoteEditHistory(campaignId!, note.id);
            fetchHistory
                .then((history) => setEditHistory(history))
                .catch(() => setEditHistory([]))
                .finally(() => setLoadingHistory(false));
        }
    }, [isOpen, note?.id, campaignId, customFetchHistoryFn]);

    const toggleHistoryItem = (id: number) => {
        setExpandedHistoryId(expandedHistoryId === id ? null : id);
    };

    const handlePinToggle = async () => {
        if (!note || !onPinToggle || isPinning) return;

        // Optimistically update the UI immediately
        const newPinnedState = !optimisticPinnedFlag;
        setOptimisticPinnedFlag(newPinnedState);
        setIsPinning(true);

        try {
            const response = await updateMessage(note.id, {
                pinned_flag: newPinnedState,
            });

            if (response.ok) {
                const updatedNote = { ...note, pinned_flag: newPinnedState };
                onPinToggle(updatedNote);
                toast.success(
                    newPinnedState
                        ? 'Note pinned successfully'
                        : 'Note unpinned successfully'
                );
            } else {
                // Revert optimistic update on error
                setOptimisticPinnedFlag(!newPinnedState);
                // Parse the error response to get the actual error message
                const errorData = await response.json();
                console.error('Failed to update note:', errorData);
                const errorMessage =
                    errorData.detail || 'Failed to update note';
                toast.error(errorMessage);
            }
        } catch (error) {
            // Revert optimistic update on error
            setOptimisticPinnedFlag(!newPinnedState);
            console.error('Error updating note:', error);
            toast.error('Failed to update note');
        } finally {
            setIsPinning(false);
        }
    };

    const handleDeleteClick = () => {
        setShowDeleteConfirm(true);
    };

    const handleDeleteConfirm = async () => {
        if (!note || !onDelete || isDeleting) return;

        setIsDeleting(true);
        toast.loading('Deleting note...');

        try {
            const response = customDeleteFn
                ? await customDeleteFn(note.id)
                : await deleteMessage(note.id);

            toast.dismiss();

            if (response.ok || response.status === 200 || response.status === 204) {
                toast.success('Note deleted successfully');
                onDelete(note.id);
                onClose();
            } else {
                const errorData = await response.json().catch(() => ({}));
                const errorMessage = errorData.detail || 'Failed to delete note';
                toast.error(errorMessage);
            }
        } catch (error) {
            toast.dismiss();
            console.error('Error deleting note:', error);
            toast.error('Failed to delete note');
        } finally {
            setIsDeleting(false);
            setShowDeleteConfirm(false);
        }
    };

    const handleDeleteCancel = () => {
        setShowDeleteConfirm(false);
    };

    useEffect(() => {
        const keyHandler = ({ keyCode }: KeyboardEvent) => {
            if (keyCode === 27) onClose(); // ESC key
        };
        if (isOpen) {
            document.addEventListener('keydown', keyHandler);
        }
        return () => document.removeEventListener('keydown', keyHandler);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <>
            <div
                className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75'
                onClick={onClose}
            >
                <div
                    onClick={(e) => e.stopPropagation()}
                    className='relative max-h-[80vh] w-full max-w-2xl rounded-lg bg-white shadow-lg'
                >
                    <div className='flex items-center justify-between p-6'>
                        <h3 className='text-gray-900 text-[28px]/[32px] font-semibold'>
                            {displayTitle}
                        </h3>
                        <div className='flex items-center gap-4'>
                            {note && onPinToggle && (
                                <div className='flex items-center gap-2'>
                                    <Toggle
                                        isOn={optimisticPinnedFlag}
                                        setIsOn={handlePinToggle}
                                    />
                                    <span className='text-sm font-normal'>Pin</span>
                                </div>
                            )}
                            <button
                                className='text-gray-400 hover:text-gray-600 transition-colors'
                                onClick={onClose}
                                aria-label='Close modal'
                            >
                                <RxCross2 size={24} />
                            </button>
                        </div>
                    </div>

                    <div className='max-h-[60vh] overflow-y-auto p-6'>
                        <div className='flex justify-between'>
                            <div className='text-[14px]/[24px]'>
                                {formatDateIfValid(note?.created_date || created_date || '', 'MM/dd/yy h:mm a')}
                            </div>
                            <div className='text-[14px]/[24px] font-bold'>
                                {note?.created_by_name || created_by_name || ''}
                            </div>
                        </div>
                        <div
                            className='text-gray-700 whitespace-pre-wrap rounded bg-[#F4F4F5] p-[8px] leading-relaxed [&_a]:text-primary [&_a]:underline [&_a]:hover:text-primary/80 [&_a]:transition-colors'
                            dangerouslySetInnerHTML={{
                                __html: linkifyUrls(displayNotes) || 'No notes available.',
                            }}
                        ></div>
                        {note?.assignee && (
                            <div className='text-[14px]/[20px] text-[#949599] mt-2'>
                                Assigned to: {note.assignee}
                            </div>
                        )}

                        {/* Edit History */}
                        {editHistory.length > 0 && (
                            <div className='mt-4'>
                                <h4 className='mb-2 text-sm font-semibold text-gray-700'>
                                    Edit History
                                </h4>
                                <div className='space-y-1'>
                                    {editHistory.map((entry) => (
                                        <div
                                            key={entry.id}
                                            className='rounded border border-gray-200'
                                        >
                                            <button
                                                onClick={() => toggleHistoryItem(entry.id)}
                                                className='flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50'
                                            >
                                                {expandedHistoryId === entry.id ? (
                                                    <FiChevronDown size={14} className='flex-shrink-0 text-gray-400' />
                                                ) : (
                                                    <FiChevronRight size={14} className='flex-shrink-0 text-gray-400' />
                                                )}
                                                <span className='text-gray-500'>
                                                    {formatDateIfValid(entry.edited_date, 'MM/dd/yy h:mm a')}
                                                </span>
                                                <span className='font-medium text-gray-700'>
                                                    Edited by {entry.edited_by_name}
                                                </span>
                                            </button>
                                            {expandedHistoryId === entry.id && (
                                                <div
                                                    className='border-t border-gray-200 bg-gray-50 px-3 py-2 text-sm [&_a]:text-primary [&_a]:underline'
                                                    dangerouslySetInnerHTML={{
                                                        __html: linkifyUrls(entry.note_content) || '<em class="text-gray-400">Empty</em>',
                                                    }}
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Delete Button - Only show if note and onDelete are provided */}
                    {note && onDelete && (
                        <div className='border-t border-gray-200 p-6'>
                            <button
                                onClick={handleDeleteClick}
                                disabled={isDeleting}
                                className='flex items-center gap-2 text-red-600 hover:text-red-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                            >
                                <FiTrash2 size={16} />
                                Delete Note
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Delete Confirmation Dialog - rendered outside the modal to avoid overflow clipping */}
            {showDeleteConfirm && (
                <div
                    className='fixed left-0 top-0 z-999999 flex h-full min-h-screen w-full items-center justify-center bg-black/90 px-4 py-5'
                    onClick={handleDeleteCancel}
                >
                    <div
                        className='relative w-full max-w-xl rounded-lg bg-white shadow-lg'
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className='p-8'>
                            <h2 className='mb-[30px] text-[22px] font-bold'>
                                Delete Note?
                            </h2>
                            <p className='mb-[30px]'>
                                Are you sure you want to delete this note? This
                                action cannot be undone.
                            </p>
                            <div className='flex justify-start space-x-4'>
                                <button
                                    onClick={handleDeleteCancel}
                                    disabled={isDeleting}
                                    className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white disabled:opacity-50 disabled:cursor-not-allowed'
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeleteConfirm}
                                    disabled={isDeleting}
                                    className='rounded-full bg-danger px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-white transition-colors duration-500 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed'
                                >
                                    {isDeleting ? 'Deleting...' : 'Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default NotesModal;
